import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import AsyncCreatableSelect from 'react-select/async-creatable';
import AsyncSelect from 'react-select/async';
import Drawer from '~/components/Drawer';
import {
    inputStyles,
    labelStyles,
    requiredStyles,
} from '~/components/Users/drawers/styles';
import {
    GrantCampaignOption,
    GrantNameOption,
    GrantNtafOption,
    createAppliedGrant,
    searchGrantCampaigns,
    searchGrantNames,
    searchGrantNtafs,
    updateAppliedGrant,
} from '~/services/grants';
import { IGrant } from '~/types/campaigns';

interface Props {
    isOpen: boolean;
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
    grant?: IGrant | null;
    onSuccess?: (grant: IGrant) => void;
}

type FormValues = {
    name: GrantNameOption | null;
    campaign: GrantCampaignOption | null;
    ntaf: GrantNtafOption | null;
    amount: string;
    date: string;
};

const defaultValues: FormValues = {
    name: null,
    campaign: null,
    ntaf: null,
    amount: '',
    date: '',
};

const asyncSelectStyles = {
    menuPortal: (base: Record<string, unknown>) => ({
        ...base,
        zIndex: 9999,
    }),
};

const ErrorMessage = ({ message }: { message: string }) => (
    <p className='mt-1 text-sm text-red'>{message}</p>
);

const ApplyGrantDrawer: React.FC<Props> = ({
    isOpen,
    setIsOpen,
    grant,
    onSuccess,
}) => {
    const isEditing = Boolean(grant?.id);
    const {
        control,
        handleSubmit,
        reset,
        setValue,
        formState: { errors, isSubmitting },
    } = useForm<FormValues>({ defaultValues });
    const [submitError, setSubmitError] = useState<string | null>(null);

    useEffect(() => {
        if (!isOpen) return;
        setSubmitError(null);
        if (grant) {
            reset({
                name: grant.name
                    ? { value: grant.name, label: grant.name }
                    : null,
                campaign:
                    grant.campaign && grant.ntaf_id
                        ? { value: grant.ntaf_id, label: grant.campaign }
                        : null,
                ntaf: grant.ntaf_id
                    ? { value: grant.ntaf_id, label: grant.ntaf_id }
                    : null,
                amount: grant.amount ?? '',
                date: grant.date ?? '',
            });
        } else {
            reset(defaultValues);
        }
    }, [isOpen, grant, reset]);

    const loadGrantOptions = async (input: string) => {
        try {
            return await searchGrantNames(input);
        } catch (err) {
            console.error('Error searching grant names:', err);
            return [];
        }
    };

    const loadCampaignOptions = async (input: string) => {
        try {
            return await searchGrantCampaigns(input, { all: true });
        } catch (err) {
            console.error('Error searching campaigns:', err);
            return [];
        }
    };

    const loadNtafOptions = async (input: string) => {
        try {
            return await searchGrantNtafs(input, { all: true });
        } catch (err) {
            console.error('Error searching NTAFs:', err);
            return [];
        }
    };

    const onSubmit = async (data: FormValues) => {
        setSubmitError(null);
        const ntafId = data.ntaf?.value || data.campaign?.value || '';
        const grantName = data.name?.value?.trim();
        if (!grantName) {
            setSubmitError('Grant is required.');
            return;
        }
        if (!ntafId) {
            setSubmitError('Campaign or NTAF ID is required.');
            return;
        }
        try {
            const payload = {
                name: grantName,
                ntaf_id: ntafId,
                amount: data.amount,
                date: data.date,
            };
            const saved =
                isEditing && grant?.id
                    ? await updateAppliedGrant(grant.id, payload)
                    : await createAppliedGrant(payload);
            toast.success(isEditing ? 'Grant updated.' : 'Grant applied.');
            onSuccess?.(saved);
            setIsOpen(false);
        } catch (err) {
            const message =
                err instanceof Error
                    ? err.message
                    : isEditing
                    ? 'Failed to update grant.'
                    : 'Failed to apply grant.';
            console.error('Failed to save grant:', err);
            toast.error(message);
            setSubmitError(message);
        }
    };

    return (
        <Drawer
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            title={isEditing ? 'Edit Grant' : 'Apply Grant'}
        >
            <form onSubmit={handleSubmit(onSubmit)}>
                <label htmlFor='apply-grant-name' className={labelStyles}>
                    Grant<span className={requiredStyles}>*</span>
                </label>
                <Controller
                    name='name'
                    control={control}
                    rules={{ required: 'Grant is required' }}
                    render={({ field }) => (
                        <AsyncCreatableSelect<GrantNameOption>
                            inputId='apply-grant-name'
                            value={field.value}
                            onChange={(option) => field.onChange(option)}
                            onCreateOption={(input) => {
                                const value = input.trim();
                                if (!value) return;
                                field.onChange({ value, label: value });
                            }}
                            loadOptions={loadGrantOptions}
                            defaultOptions
                            placeholder='Select'
                            isClearable
                            cacheOptions
                            menuPortalTarget={document.body}
                            menuPosition='fixed'
                            styles={asyncSelectStyles}
                            formatCreateLabel={(input) => `Use "${input}"`}
                            noOptionsMessage={({ inputValue }) =>
                                inputValue
                                    ? `Type to add "${inputValue}"`
                                    : 'Type to search grants'
                            }
                        />
                    )}
                />
                {errors.name && <ErrorMessage message='Grant is required' />}

                <div className='flex flex-wrap gap-4'>
                    <div className='min-w-[220px] flex-1'>
                        <label
                            htmlFor='apply-grant-campaign'
                            className={labelStyles}
                        >
                            Campaign<span className={requiredStyles}>*</span>
                        </label>
                        <Controller
                            name='campaign'
                            control={control}
                            render={({ field }) => (
                                <AsyncSelect<GrantCampaignOption>
                                    inputId='apply-grant-campaign'
                                    value={field.value}
                                    onChange={(option) => {
                                        field.onChange(option);
                                        if (option) {
                                            setValue('ntaf', {
                                                value: option.value,
                                                label: option.value,
                                            });
                                        }
                                    }}
                                    loadOptions={loadCampaignOptions}
                                    defaultOptions
                                    placeholder='Start typing...'
                                    isClearable
                                    cacheOptions
                                    menuPortalTarget={document.body}
                                    menuPosition='fixed'
                                    styles={asyncSelectStyles}
                                    noOptionsMessage={({ inputValue }) =>
                                        inputValue
                                            ? `No campaigns matching "${inputValue}"`
                                            : 'Type to search campaigns'
                                    }
                                />
                            )}
                        />
                    </div>
                    <div className='w-[160px]'>
                        <label
                            htmlFor='apply-grant-ntaf'
                            className={labelStyles}
                        >
                            NTAF ID<span className={requiredStyles}>*</span>
                        </label>
                        <Controller
                            name='ntaf'
                            control={control}
                            render={({ field }) => (
                                <AsyncSelect<GrantNtafOption>
                                    inputId='apply-grant-ntaf'
                                    value={field.value}
                                    onChange={(option) =>
                                        field.onChange(option)
                                    }
                                    loadOptions={loadNtafOptions}
                                    defaultOptions
                                    placeholder='Search'
                                    isClearable
                                    cacheOptions
                                    menuPortalTarget={document.body}
                                    menuPosition='fixed'
                                    styles={asyncSelectStyles}
                                    noOptionsMessage={({ inputValue }) =>
                                        inputValue
                                            ? `No NTAFs matching "${inputValue}"`
                                            : 'Type to search NTAFs'
                                    }
                                />
                            )}
                        />
                    </div>
                </div>

                <label htmlFor='apply-grant-amount' className={labelStyles}>
                    Amount<span className={requiredStyles}>*</span>
                </label>
                <Controller
                    name='amount'
                    control={control}
                    rules={{
                        required: 'Amount is required',
                        pattern: {
                            value: /^\d+(\.\d{1,2})?$/,
                            message: 'Enter a valid amount (e.g. 100.00)',
                        },
                    }}
                    render={({ field }) => (
                        <input
                            id='apply-grant-amount'
                            type='text'
                            inputMode='decimal'
                            placeholder='$0.00'
                            className={`${inputStyles} w-40`}
                            {...field}
                        />
                    )}
                />
                {errors.amount && (
                    <ErrorMessage message={errors.amount.message ?? ''} />
                )}

                <label htmlFor='apply-grant-date' className={labelStyles}>
                    Date<span className={requiredStyles}>*</span>
                </label>
                <Controller
                    name='date'
                    control={control}
                    rules={{ required: 'Date is required' }}
                    render={({ field }) => (
                        <input
                            id='apply-grant-date'
                            type='date'
                            className={`${inputStyles} w-48`}
                            {...field}
                        />
                    )}
                />
                {errors.date && (
                    <ErrorMessage message={errors.date.message ?? ''} />
                )}

                {submitError && <ErrorMessage message={submitError} />}

                <div className='mt-[30px] flex flex-wrap gap-[16px]'>
                    <button
                        type='button'
                        onClick={() => setIsOpen(false)}
                        disabled={isSubmitting}
                        className='rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-[#E6F7F8] hover:bg-[#E6F7F8]'
                    >
                        Cancel
                    </button>
                    <button
                        type='submit'
                        disabled={isSubmitting}
                        className='rounded-full border-[2px] border-[#FEBF3E] bg-[#FEBF3E] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-black transition-colors duration-500 hover:border-[#E6F7F8] hover:bg-[#E6F7F8]'
                    >
                        {isSubmitting ? 'Saving...' : 'Save'}
                    </button>
                </div>
            </form>
        </Drawer>
    );
};

export default ApplyGrantDrawer;
